import { useState, useEffect, useCallback } from 'react'
import {
  getListings,
  getListing,
  approveListing,
  rejectListing,
  removeListing,
  bulkListingAction,
  type ListingSummary,
  type ListingDetail,
  type ListingFilters,
} from '../../services/adminApi'
import { ListingCard } from '../../components/cards/ListingCard'
import { ListingDetailModal } from './ListingDetailModal'
import { CustomButton } from '../../components/common/CustomButton'
import { CustomTextInput } from '../../components/common/CustomTextInput'
import { usePermission } from '../../hooks/usePermission'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'

const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'active', label: 'Active' },
  { value: 'draft', label: 'Draft' },
  { value: 'sold', label: 'Sold' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'removed', label: 'Removed' },
]

export function Listings() {
  const [listings, setListings] = useState<ListingSummary[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [filters, setFilters] = useState<ListingFilters>({
    status: '',
    search: '',
    page: 1,
    page_size: 20,
  })

  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebouncedValue(searchInput, 300)

  useEffect(() => {
    setFilters((prev) => (prev.search === debouncedSearch ? prev : { ...prev, search: debouncedSearch, page: 1 }))
  }, [debouncedSearch])

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [selectedListing, setSelectedListing] = useState<ListingDetail | null>(null)
  const [bulkAction, setBulkAction] = useState<string | null>(null)
  const [bulkReason, setBulkReason] = useState('')

  const canDelete = usePermission('listings', 'delete')

  const loadListings = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await getListings(filters)
      if (response.ok && response.data) {
        setListings(response.data.items)
        setTotal(response.data.total)
        setPage(response.data.page)
        setTotalPages(response.data.total_pages)
      } else {
        setError(response.error?.message || 'Failed to load listings')
      }
    } catch {
      setError('Failed to connect to API')
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    loadListings()
  }, [loadListings])

  const handleFilterChange = (key: keyof ListingFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }))
  }

  const handlePageChange = (newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }))
  }

  const handleSelectListing = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const handleSelectAll = () => {
    if (selectedIds.size === listings.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(listings.map((l) => l.id)))
    }
  }

  const handleOpenDetail = async (listing: ListingSummary) => {
    const response = await getListing(listing.id)
    if (response.ok && response.data) {
      setSelectedListing(response.data)
    }
  }

  const handleApprove = async (notes?: string) => {
    if (!selectedListing) return
    const response = await approveListing(selectedListing.id, notes)
    if (response.ok) {
      loadListings()
    }
  }

  const handleReject = async (reason: string, notes?: string) => {
    if (!selectedListing) return
    const response = await rejectListing(selectedListing.id, reason, notes)
    if (response.ok) {
      loadListings()
    }
  }

  const handleRemove = async (reason: string) => {
    if (!selectedListing) return
    const response = await removeListing(selectedListing.id, reason)
    if (response.ok) {
      loadListings()
    }
  }

  const handleBulkAction = async () => {
    if (!bulkAction || selectedIds.size === 0) return

    const response = await bulkListingAction(
      Array.from(selectedIds),
      bulkAction,
      bulkReason || undefined
    )

    if (response.ok) {
      setSelectedIds(new Set())
      setBulkAction(null)
      setBulkReason('')
      loadListings()
    }
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-gray-900">Listings</h2>
          <p className="text-sm text-gray-500">{total} total listings</p>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap gap-4">
        <div className="w-64">
          <CustomTextInput
            label=""
            placeholder="Search listings..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <select
          value={filters.status || ''}
          onChange={(e) => handleFilterChange('status', e.target.value)}
          className="rounded-xl border border-gray-300 px-4 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          {statusOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Bulk Actions */}
      {canDelete && selectedIds.size > 0 && (
        <div className="mb-6 rounded-xl bg-gray-50 dark:bg-[#1a1520] dark:border dark:border-[#3d2c4f] p-4">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
              {selectedIds.size} selected
            </span>
            <select
              value={bulkAction || ''}
              onChange={(e) => setBulkAction(e.target.value)}
              className="rounded-lg border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#2a1f35] text-gray-900 dark:text-gray-100 px-3 py-1.5 text-sm"
            >
              <option value="">Select action...</option>
              <option value="approve">Approve</option>
              <option value="reject">Reject</option>
              <option value="remove">Remove</option>
            </select>
            {bulkAction && bulkAction !== 'approve' && (
              <input
                type="text"
                placeholder="Reason..."
                value={bulkReason}
                onChange={(e) => setBulkReason(e.target.value)}
                className="rounded-lg border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#2a1f35] text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 px-3 py-1.5 text-sm"
              />
            )}
            {bulkAction && (
              <CustomButton
                onClick={handleBulkAction}
                variant="primary"
                disabled={bulkAction !== 'approve' && !bulkReason}
              >
                Apply
              </CustomButton>
            )}
            <button
              onClick={() => setSelectedIds(new Set())}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              Clear selection
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mb-6 rounded-xl bg-red-50 p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Listings Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : listings.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          No listings found
        </div>
      ) : (
        <>
          {canDelete && (
            <div className="mb-4">
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={selectedIds.size === listings.length && listings.length > 0}
                  onChange={handleSelectAll}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                Select all
              </label>
            </div>
          )}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {listings.map((listing) => (
              <ListingCard
                key={listing.id}
                listing={listing}
                selected={selectedIds.has(listing.id)}
                onSelect={canDelete ? handleSelectListing : undefined}
                onClick={handleOpenDetail}
              />
            ))}
          </div>
        </>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          <CustomButton
            onClick={() => handlePageChange(page - 1)}
            disabled={page <= 1}
            variant="secondary"
          >
            Previous
          </CustomButton>
          <span className="text-sm text-gray-600">
            Page {page} of {totalPages}
          </span>
          <CustomButton
            onClick={() => handlePageChange(page + 1)}
            disabled={page >= totalPages}
            variant="secondary"
          >
            Next
          </CustomButton>
        </div>
      )}

      {/* Detail Modal */}
      {selectedListing && (
        <ListingDetailModal
          listing={selectedListing}
          onClose={() => setSelectedListing(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          onRemove={handleRemove}
        />
      )}
    </div>
  )
}
